// Copyright (c) Ping. All rights reserved.
//
// Web host for shared `createGroupConversation`. Wires Dexie, claim-fanout,
// session transport, and local row persistence. The UI calls this from
// NewGroupModal.

import {
  createGroupConversation,
  type CreateGroupConversationResult,
  type GroupMemberInput,
  type MessagingClientLike,
  normalizeGroupDeviceRosterMaps,
  normalizePeerAccountIds,
  sendGroupHydrationMessages,
} from '@ping/ui';

import { log } from '@core/log/log';
import { api } from '@core/network';
import { claimDevicesForGroupMember } from '@core/network/users';
import { retrieveIdentity } from '@core/persistence/identity';
import { getCurrentTransport } from '@session/sessionBootstrap';

import { ensureGroupConversation } from './ensureConversation';
import { kvAdapter } from './mlsConversation';
import { webConversationsAdapter } from './webConversationsStore';

export type { CreateGroupConversationResult, GroupMemberInput };
export { GroupCreateError } from '@ping/ui';

export interface CreateWebGroupConversationParams {
  readonly client: MessagingClientLike;
  readonly members: readonly GroupMemberInput[];
  readonly displayName?: string;
  /** Local (data-URL) avatar for the immediate local row + hydration broadcast. */
  readonly avatarUri?: string;
  /**
   * Media id (base64url) of the uploaded group avatar. Embedded in the group
   * meta (GroupContext) so it propagates to joiners via group state. The host
   * uploads the avatar before calling this and passes the resulting media id.
   */
  readonly avatarMediaId?: string;
  readonly signal?: AbortSignal;
}

/**
 * Mint (or dedup) an MLS group, invite all peer devices, persist the
 * local Dexie row, and send optional hydration app events.
 */
export async function createWebGroupConversation(
  params: CreateWebGroupConversationParams,
): Promise<CreateGroupConversationResult> {
  const identity = await retrieveIdentity();
  if (identity === null) {
    throw new Error('Still signing you in. Try again in a moment.');
  }

  // Claim our OWN other devices (linked siblings, excluding this device) so the
  // group is delivered to every device of the account in the SAME Commit — the
  // conversation then appears on them in realtime, not only after a later link.
  // Best-effort: a self-claim failure must never block creating the group.
  const selfSiblingDevices = (
    await claimDevicesForGroupMember(identity.accountId).catch(() => [])
  ).filter((d) => d.deviceIdHex !== identity.deviceId);

  const result = await createGroupConversation({
    client: params.client,
    transport: getCurrentTransport(),
    api,
    kv: kvAdapter,
    conversations: webConversationsAdapter,
    log,
    localAccountId: identity.accountId,
    localDisplayName: identity.displayName,
    // Register the group's kind server-side (kind:'group_chat') before the
    // invite so the BE never lazily defaults it to direct_message.
    localDeviceId: identity.deviceId,
    members: params.members,
    ...(params.displayName !== undefined ? { displayName: params.displayName } : {}),
    ...(params.avatarMediaId !== undefined ? { avatarMediaId: params.avatarMediaId } : {}),
    claimDevicesForAccount: claimDevicesForGroupMember,
    ...(selfSiblingDevices.length > 0 ? { selfSiblingDevices } : {}),
    ...(params.signal !== undefined ? { signal: params.signal } : {}),
  });

  const peerAccountIds = normalizePeerAccountIds(identity.accountId, params.members);

  const memberDisplayNames: Record<string, string> = {};
  for (const m of params.members) {
    if (m.accountId !== identity.accountId) {
      memberDisplayNames[m.accountId] = m.displayName;
    }
  }

  const rosterPatch = normalizeGroupDeviceRosterMaps({
    memberDeviceNames: result.memberDeviceNames,
    memberDeviceAccounts: result.memberDeviceAccounts,
  });

  await ensureGroupConversation({
    conversationId: result.conversationIdHex,
    displayName: result.displayName,
    memberAccountIds: peerAccountIds,
    memberDisplayNames,
    ...(rosterPatch.memberDeviceNames !== undefined
      ? { memberDeviceNames: rosterPatch.memberDeviceNames }
      : {}),
    ...(rosterPatch.memberDeviceAccounts !== undefined
      ? { memberDeviceAccounts: rosterPatch.memberDeviceAccounts }
      : {}),
    ...(params.avatarUri !== undefined ? { avatarUri: params.avatarUri } : {}),
    // Persist the stable media id (not just the expiring presigned URL) so the
    // group icon survives URL expiry + roster-change churn.
    ...(params.avatarMediaId !== undefined ? { avatarMediaId: params.avatarMediaId } : {}),
    // C3: the creator is the group OWNER — bootstraps role authority.
    ownerAccountId: identity.accountId,
  });

  // Subscribe the freshly-created group for realtime immediately so the CREATOR
  // receives members' replies without waiting for the next `App.tsx` reconcile
  // pass (the new conversation has no WS subscription until then). Idempotent +
  // best-effort.
  void getCurrentTransport()
    ?.subscribeToConversations([result.conversationIdHex])
    .catch(() => undefined);

  if (!result.deduped) {
    await sendGroupHydrationMessages({
      conv: result.conv,
      inviterAccountId: identity.accountId,
      inviterDisplayName: identity.displayName,
      title: params.displayName?.trim(),
      memberAccountIds: peerAccountIds,
      memberDisplayNames,
      ...(params.avatarUri !== undefined ? { avatarUri: params.avatarUri } : {}),
      // CRITICAL: carry the STABLE group-avatar media id on the hydration
      // broadcast, not just the inline data-URL. Without it a joiner (B) sets
      // its row's `avatarUri` from the data-URL but leaves `avatarMediaId`
      // unset — so when `hydrateMemberProfiles` runs on chat-open it either
      // re-resolves a STALE/poison id (a single-peer group briefly mis-seeded
      // as a 1:1 stamps the PEER's avatar media id here) or has nothing to
      // re-resolve, and the correct group icon is clobbered by the other
      // member's face. Passing the id makes every joiner store the GROUP's own
      // media id (which the group-branch hydration then re-resolves) — the
      // authoritative fix for the "single-peer group avatar becomes the other
      // member's the moment I open the chat" bleed. Mirrors the MLS-meta path
      // (createGroupConversation above) and the local row (ensureGroupConversation).
      ...(params.avatarMediaId !== undefined ? { avatarMediaId: params.avatarMediaId } : {}),
      log,
    });
  }

  log.info({
    event: 'webGroup.create.ok',
    conversationIdPrefix: result.conversationIdHex.slice(0, 8),
    peerCount: peerAccountIds.length,
    deduped: result.deduped,
  });

  return result;
}
